import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import useRootStore from '@store/useRootStore'
import { ListView } from 'antd-mobile'
import AdvImg from '@components/AdvImg'
import classNames from 'classnames'
import goldNumberIcon from '@assets/images/icon/ranking_event_icon/ranking_1.png'
import silverNumberIcon from '@assets/images/icon/ranking_event_icon/ranking_2.png'
import bronzeNumberIcon from '@assets/images/icon/ranking_event_icon/ranking_3.png'
import rankingEmptyIcon from '@assets/images/icon/ranking_event_icon/ranking_empty.png'
import { formatNumber } from '@utils/utils'

const PAGE_SIZE = 20
const MAX_RANKINGS = 100

const ITEM_HEIGHT = 45
const ITEM_GAP = 11
const LIST_MAX_HEIGHT = 540

interface Props {
  eventId: number
  isPending?: boolean
}

const RankingList: React.FC<Props> = observer(({ eventId, isPending }) => {
  const { rankingEventStore, authStore } = useRootStore()
  const { rankings, totalCount } = rankingEventStore

  const [offset, setOffset] = useState(0)
  const [dataSource, setDataSource] = useState(new ListView.DataSource({
    rowHasChanged: (row1, row2) => row1 !== row2,
  }))

  useEffect(() => {
    // 이벤트가 변경될 때마다 리스트 초기화 및 첫 페이지 로드
    setOffset(0)
    rankingEventStore.fetchRankingBoard(eventId, 0, PAGE_SIZE).catch(console.error)
  }, [eventId, rankingEventStore])

  const handleLoadMore = () => {
    const nextOffset = offset + PAGE_SIZE
    if (nextOffset < MAX_RANKINGS && nextOffset < totalCount) {
      setOffset(nextOffset)
      rankingEventStore.fetchRankingBoard(eventId, nextOffset, PAGE_SIZE).catch(console.error)
    }
  }

  const hasMore = rankings.length < totalCount && rankings.length < MAX_RANKINGS

  const renderRow = (item: any) => {
    const { rank, score, userInfo } = item
    const avatar = userInfo?.userProfileImg
    const isMe = !!authStore.userInfo?.id && item.userPromiseId === authStore.userInfo.id
    const name = isMe ? (authStore.userInfo?.nick || '-') : (userInfo?.userNick || '-')

    const profileClass = classNames('ranking-item--profile', {
      'gold': rank === 1,
      'silver': rank === 2,
      'bronze': rank === 3,
    })

    return (
      <div className="ranking-item">
        <div className="ranking-item--left-section">
          <span className="ranking-item--ranking">
            {rank === 1
              ? <img className="ranking-number-icon" src={goldNumberIcon} alt="1위"/>
              : rank === 2
                ? <img className="ranking-number-icon" src={silverNumberIcon} alt="2위"/>
                : rank === 3
                  ? <img className="ranking-number-icon" src={bronzeNumberIcon} alt="3위"/>
                  : rank
            }
          </span>
          <span className={profileClass}>
            <AdvImg
              src={avatar}
              srcMode="small"
              alt="프로필사진"
              className="profile-image"
            />
          </span>
          <span className="ranking-item--name">{name}</span>
        </div>
        <span className="ranking-item--score">{formatNumber(score)}점</span>
      </div>
    )
  }

  useEffect(() => {
    setDataSource(dataSource.cloneWithRows(rankings))
  }, [rankings])

  const listHeight =
    Math.min(rankings.length * ITEM_HEIGHT + (rankings.length - 1) * ITEM_GAP, LIST_MAX_HEIGHT)

  return (
    <div className={classNames('ranking-list', { 'empty': rankings.length === 0 })}>
      {rankings.length > 0 ? (
        <ListView
          dataSource={dataSource}
          renderRow={renderRow}
          onEndReached={hasMore && handleLoadMore}
          onEndReachedThreshold={10}
          useBodyScroll={false}
          pageSize={PAGE_SIZE}
          className="ranking-list--wrap"
          style={{ height: listHeight }}
        />
      ) : (
        <div className="ranking-list--empty">
          <img src={rankingEmptyIcon} alt="랭킹이 없습니다." className="empty-icon"/>
          {isPending ? (
            <p className="empty-title">랭킹 이벤트가 진행될 예정입니다.</p>
          ) : (
            <>
              <p className="empty-title">참여한 유저가 없습니다.</p>
              <p className="empty-desc">랭킹 이벤트에 도전하세요!</p>
            </>
          )}
        </div>
      )}
    </div>
  )
})

export default RankingList
